import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixRestaurantsMenuSettingsV1MenuOrderingSettingsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'bo._base_domain_': [
      {
        srcPath: '/_api/menu-ordering-settings',
        destPath: '',
      },
    ],
    'wixbo.ai': [
      {
        srcPath: '/_api/menu-ordering-settings',
        destPath: '',
      },
    ],
    'wix-bo.com': [
      {
        srcPath: '/_api/menu-ordering-settings',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/menu-ordering-settings',
        destPath: '',
      },
    ],
    'editor.wixapps.net': [
      {
        srcPath: '/_api/menu-ordering-settings',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/menu-ordering-settings',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/menu-ordering-settings',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/menu-ordering-settings',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/menu-ordering-settings',
        destPath: '',
      },
    ],
    _base_domain_: [
      {
        srcPath: '/_api/menu-ordering-settings',
        destPath: '',
      },
    ],
    'users._base_domain_': [
      {
        srcPath: '/_api/menu-ordering-settings',
        destPath: '',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/menu-ordering-settings',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/menu-ordering-settings',
        destPath: '',
      },
    ],
    'api._api_base_domain_': [
      {
        srcPath: '/menu-ordering-settings',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/menu-ordering-settings',
        destPath: '',
      },
    ],
    'apps._base_domain_': [
      {
        srcPath: '/_api/menu-ordering-settings',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_restaurants_menu-ordering-settings';

/** Retrieves a menu ordering settings entity. */
export function getMenuOrderingSettings(
  payload: object
): RequestOptionsFactory<any> {
  function __getMenuOrderingSettings({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.menu_settings.v1.menu_ordering_settings',
      method: 'GET' as any,
      methodFqn:
        'wix.restaurants.menu_settings.v1.MenuOrderingSettingsService.GetMenuOrderingSettings',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsMenuSettingsV1MenuOrderingSettingsServiceUrl({
        protoPath: '/v1/menu-ordering-settings/{menuOrderingSettingsId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'menuOrderingSettings.createdDate' },
              { path: 'menuOrderingSettings.updatedDate' },
              {
                path: 'menuOrderingSettings.availability.timestampRangesOptions.ranges.startTime',
              },
              {
                path: 'menuOrderingSettings.availability.timestampRangesOptions.ranges.endTime',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getMenuOrderingSettings;
}

/**
 * Upserts a menu ordering settings entity for a given `menuId`.
 * Try to create a menu ordering settings, in case it already exists - update the menu ordering settings
 */
export function upsertMenuOrderingSettingsByMenuId(
  payload: object
): RequestOptionsFactory<any> {
  function __upsertMenuOrderingSettingsByMenuId({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'menuOrderingSettings.createdDate' },
          { path: 'menuOrderingSettings.updatedDate' },
          {
            path: 'menuOrderingSettings.availability.timestampRangesOptions.ranges.startTime',
          },
          {
            path: 'menuOrderingSettings.availability.timestampRangesOptions.ranges.endTime',
          },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.restaurants.menu_settings.v1.menu_ordering_settings',
      method: 'POST' as any,
      methodFqn:
        'wix.restaurants.menu_settings.v1.MenuOrderingSettingsService.UpsertMenuOrderingSettingsByMenuId',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsMenuSettingsV1MenuOrderingSettingsServiceUrl({
        protoPath:
          '/v1/menu-ordering-settings/upsert/menu-id/{menuOrderingSettings.menuId}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'menuOrderingSettings.createdDate' },
              { path: 'menuOrderingSettings.updatedDate' },
              {
                path: 'menuOrderingSettings.availability.timestampRangesOptions.ranges.startTime',
              },
              {
                path: 'menuOrderingSettings.availability.timestampRangesOptions.ranges.endTime',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __upsertMenuOrderingSettingsByMenuId;
}

/**
 * Updates a menu ordering settings entity.
 *
 * Each time the menu ordering settings entity is updated, `revision` increments by 1. The current `revision` must be specified when updating the menu ordering settings entity.
 * This ensures you're working with the latest entity and prevents unintended overwrites.
 */
export function updateMenuOrderingSettings(
  payload: object
): RequestOptionsFactory<any> {
  function __updateMenuOrderingSettings({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'menuOrderingSettings.createdDate' },
          { path: 'menuOrderingSettings.updatedDate' },
          {
            path: 'menuOrderingSettings.availability.timestampRangesOptions.ranges.startTime',
          },
          {
            path: 'menuOrderingSettings.availability.timestampRangesOptions.ranges.endTime',
          },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.restaurants.menu_settings.v1.menu_ordering_settings',
      method: 'PATCH' as any,
      methodFqn:
        'wix.restaurants.menu_settings.v1.MenuOrderingSettingsService.UpdateMenuOrderingSettings',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsMenuSettingsV1MenuOrderingSettingsServiceUrl({
        protoPath: '/v1/menu-ordering-settings/{menuOrderingSettings.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'menuOrderingSettings.createdDate' },
              { path: 'menuOrderingSettings.updatedDate' },
              {
                path: 'menuOrderingSettings.availability.timestampRangesOptions.ranges.startTime',
              },
              {
                path: 'menuOrderingSettings.availability.timestampRangesOptions.ranges.endTime',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateMenuOrderingSettings;
}

/**
 * Creates a query to retrieve a list of menu ordering settings objects.
 *
 * The `queryMenuOrderingSettings()` function builds a query to retrieve a list of menu ordering settings objects and returns an `MenuOrderingSettingsQueryBuilder` object.
 *
 * The returned object contains the query definition, which is used to run the query using the [`find()`](/menu-ordering-settings/menu-ordering-settings-query-builder/find) function.
 *
 * You can refine the query by chaining `MenuOrderingSettingsQueryBuilder` functions onto the query. `MenuOrderingSettingsQueryBuilder` functions enable you to filter, sort, and control the results that `queryMenuOrderingSettings()` returns.
 *
 * `queryMenuOrderingSettings()` runs with the following `MenuOrderingSettingsQueryBuilder` defaults, which you can override:
 *
 * - [`limit(50)`](/menu-ordering-settings/menu-ordering-settings-query-builder/limit)
 * - [ascending('_id')](/menu-ordering-settings/menu-ordering-settings-query-builder/ascending)
 *
 * The following `MenuOrderingSettingsQueryBuilder` functions are supported for `queryMenuOrderingSettings()`. For a full description of the menu ordering settings object, see the object returned for the [`items`](/menu-ordering-settings/menu-ordering-settings-query-result/items) property in `MenuOrderingSettingsQueryResult`.
 */
export function queryMenuOrderingSettings(
  payload: object
): RequestOptionsFactory<any> {
  function __queryMenuOrderingSettings({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.menu_settings.v1.menu_ordering_settings',
      method: 'POST' as any,
      methodFqn:
        'wix.restaurants.menu_settings.v1.MenuOrderingSettingsService.QueryMenuOrderingSettings',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsMenuSettingsV1MenuOrderingSettingsServiceUrl({
        protoPath: '/v1/menu-ordering-settings/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'menuOrderingSettings.createdDate' },
              { path: 'menuOrderingSettings.updatedDate' },
              {
                path: 'menuOrderingSettings.availability.timestampRangesOptions.ranges.startTime',
              },
              {
                path: 'menuOrderingSettings.availability.timestampRangesOptions.ranges.endTime',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryMenuOrderingSettings;
}

/**
 * Updates multiple menu ordering settings entities at once.
 * Each time a menu ordering settings entity is updated, `revision` increments by 1. The existing revision must be specified when updating the menu ordering settings entity. This ensures you're working with the entity's latest information, and it prevents unintended overwrites.
 */
export function bulkUpdateMenuOrderingSettings(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateMenuOrderingSettings({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'menusOrderingSettings.mask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'menusOrderingSettings.menuOrderingSettings.createdDate' },
          { path: 'menusOrderingSettings.menuOrderingSettings.updatedDate' },
          {
            path: 'menusOrderingSettings.menuOrderingSettings.availability.timestampRangesOptions.ranges.startTime',
          },
          {
            path: 'menusOrderingSettings.menuOrderingSettings.availability.timestampRangesOptions.ranges.endTime',
          },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.restaurants.menu_settings.v1.menu_ordering_settings',
      method: 'POST' as any,
      methodFqn:
        'wix.restaurants.menu_settings.v1.MenuOrderingSettingsService.BulkUpdateMenuOrderingSettings',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsMenuSettingsV1MenuOrderingSettingsServiceUrl({
        protoPath: '/v1/bulk/menu-ordering-settings/update',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.menuOrderingSettings.createdDate' },
              { path: 'results.menuOrderingSettings.updatedDate' },
              {
                path: 'results.menuOrderingSettings.availability.timestampRangesOptions.ranges.startTime',
              },
              {
                path: 'results.menuOrderingSettings.availability.timestampRangesOptions.ranges.endTime',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkUpdateMenuOrderingSettings;
}

/**
 * Retrieves a list of a menu's availability statuses for a given time slot and restaurant operation. (See the Restaurants Operations API for more information.)
 * Returns the availability status for the given time slot per menu.
 */
export function listMenusAvailabilityStatus(
  payload: object
): RequestOptionsFactory<any> {
  function __listMenusAvailabilityStatus({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'timeSlot.startTime' }, { path: 'timeSlot.endTime' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.restaurants.menu_settings.v1.menu_ordering_settings',
      method: 'GET' as any,
      methodFqn:
        'wix.restaurants.menu_settings.v1.MenuOrderingSettingsService.ListMenusAvailabilityStatus',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsMenuSettingsV1MenuOrderingSettingsServiceUrl({
        protoPath: '/v1/menu-ordering-settings/menus-availability-status',
        data: serializedData,
        host,
      }),
      params: toURLSearchParams(serializedData),
    };

    return metadata;
  }

  return __listMenusAvailabilityStatus;
}

/** Updates extended fields of a menu ordering settings entity without incrementing its revision. */
export function updateExtendedFields(
  payload: object
): RequestOptionsFactory<any> {
  function __updateExtendedFields({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.menu_settings.v1.menu_ordering_settings',
      method: 'POST' as any,
      methodFqn:
        'wix.restaurants.menu_settings.v1.MenuOrderingSettingsService.UpdateExtendedFields',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsMenuSettingsV1MenuOrderingSettingsServiceUrl({
        protoPath: '/v1/menu-ordering-settings/{id}/update-extended-fields',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'menuOrderingSettings.createdDate' },
              { path: 'menuOrderingSettings.updatedDate' },
              {
                path: 'menuOrderingSettings.availability.timestampRangesOptions.ranges.startTime',
              },
              {
                path: 'menuOrderingSettings.availability.timestampRangesOptions.ranges.endTime',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateExtendedFields;
}

/**
 * Synchronously update tags on multiple menu ordering settings entities.
 * If you specify a tag in both `assignTags` and `unassignTags`, it is assigned.
 */
export function bulkUpdateMenuOrderingSettingsTags(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateMenuOrderingSettingsTags({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.menu_settings.v1.menu_ordering_settings',
      method: 'POST' as any,
      methodFqn:
        'wix.restaurants.menu_settings.v1.MenuOrderingSettingsService.BulkUpdateMenuOrderingSettingsTags',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsMenuSettingsV1MenuOrderingSettingsServiceUrl({
        protoPath: '/v1/bulk/menu-ordering-settings/update-tags',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkUpdateMenuOrderingSettingsTags;
}

/**
 * Asynchronously update tags on multiple menu ordering settings entities according to the specified filter.
 * If a filter isn't specified, this method updates all menu ordering settings entities.
 * If you specify a tag in both `assignTags` and `unassignTags`, it is assigned.
 */
export function bulkUpdateMenuOrderingSettingsTagsByFilter(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateMenuOrderingSettingsTagsByFilter({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.menu_settings.v1.menu_ordering_settings',
      method: 'POST' as any,
      methodFqn:
        'wix.restaurants.menu_settings.v1.MenuOrderingSettingsService.BulkUpdateMenuOrderingSettingsTagsByFilter',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsMenuSettingsV1MenuOrderingSettingsServiceUrl({
        protoPath: '/v1/bulk/menu-ordering-settings/update-tags-by-filter',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkUpdateMenuOrderingSettingsTagsByFilter;
}
